import {
  FluidAlignment,
  FluidFlexAlignment,
  FluidGridItemCargo,
  FluidGridItemConfig,
  FluidGridItemSize,
  generateUniqueId,
  isNullOrUndefined,
  isNumber,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Host,
  Prop,
  Watch,
} from '@stencil/core';

import {
  GRID_ITEM_COLUMN_VALIDATION,
  GRID_ITEM_ORDER_VALIDATION,
} from '../../../constants/constants';
import { Utils } from '../../../utils/utils';

/**
 * @displayName Grid Item
 *
 * @contextData FLUID Grid Items are the foundational building blocks of FLUID Grid - they create modular, flexible columns for page content that can be customized at 5 incremental responsive breakpoints.
 *
 * @childOf fluid-grid
 *
 * @slot - Content displayed within the Grid Item
 */
@Component({
  tag: 'fluid-grid-item',
  styleUrl: 'fluid-grid-item.less',
})
export class FluidGridItem implements ComponentInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Internal config state containing all column widths.
   */
  private _columnConfig: FluidGridItemCargo;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidGridItemElement;

  // ================================================================ //
  // -- Props

  /**
   * Sets default column width at smallest mobile breakpoint size & up
   * (viewport sizes: >0px). Accepts number value 1-12, "auto", or "fill".
   * NOTE: when setting breakpoint sizes larger than "colSm", it's
   * recommended to ALWAYS set this Prop's value to 12 - this provides
   * a "fallback" value for smaller breakpoint sizes (otherwise, smaller
   * breakpoints will behave as if no "col-..." Prop is set).
   */
  @Prop({ mutable: true }) col: FluidGridItemSize;

  /**
   * Sets column width at large mobile / small tablet breakpoint size & up
   * (viewport sizes: ≥480px). Accepts number value 1-12, "auto", or "fill".
   */
  @Prop({ mutable: true }) colSm: FluidGridItemSize;

  /**
   * Sets column width at tablet / small desktop breakpoint size & up
   * (viewport sizes: ≥768px). Accepts number value 1-12, "auto", or "fill".
   */
  @Prop({ mutable: true }) colMd: FluidGridItemSize;

  /**
   * Sets column width at desktop breakpoint size & up
   * (viewport sizes: ≥992px). Accepts number value 1-12, "auto", or "fill".
   */
  @Prop({ mutable: true }) colLg: FluidGridItemSize;

  /**
   * Sets column width at large desktop breakpoint size & up
   * (viewport sizes: ≥1200px). Accepts number value 1-12, "auto", or "fill".
   */
  @Prop({ mutable: true }) colXl: FluidGridItemSize;

  /**
   * Sets column width automatically based on content width at ALL
   * breakpoint sizes (i.e. "fluid" column width - no pun intended 😏).
   * To use "auto width" at specific breakpoint sizes, set any of the
   * "col-..." Props to "auto". NOTE: this Prop is overridden by any/
   * all other "col-..." Props applied to the Grid Item, so it needs
   * to be used independently of them to work correctly.
   */
  @Prop({ mutable: true }) auto = false;

  /**
   * Set vertical alignment for a single Grid Item within parent Grid.
   * Overrides Grid parent's vertical alignment setting (i.e.
   * "alignItems" Prop). NOTE: Grid Item's child(ren) MUST be shorter
   * than the height of the parent Grid for this Prop to work.
   */
  @Prop({ mutable: true }) alignSelf: FluidFlexAlignment;

  /**
   * When provided, changes default DOM order of the Grid Item within
   * its Grid parent (i.e. sets flexbox "order" property). By default,
   * all Grid Items have an order of 0 - changing this Prop to 1 will
   * cause the Grid Item to be displayed AFTER other Grid Item siblings.
   */
  @Prop({ mutable: true }) order: number;

  @Watch('order')
  onOrderChange() {
    this.validateOrder();
  }

  /**
   * Right-justify a single Grid Item within parent Grid. Only applicable
   * when sum of all GridItem "col" values is less than 12. Works best
   * when parent Grid's "justify" Prop is "start".
   */
  @Prop({ mutable: true }) pullRight: boolean;

  /**
   * Left-justify a single Grid Item within parent Grid. Only applicable
   * when sum of all GridItem "col" values is less than 12. Works best
   * when parent Grid's "justify" Prop is "end".
   */
  @Prop({ mutable: true }) pullLeft: boolean;

  /**
   * Allows all Grid Item Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidGridItemConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the grid-item in order
   * to help facilitate testing.
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action selectiveMapConfigToLocal - set local Props to "config" values
   */
  connectedCallback() {
    Utils.selectiveMapConfigToLocal(this, this.config);
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action gridItemCargoDeprecationWarning - deprecation warning for "gridItemCargo"
   * @action updateColumnConfig - updates internal column width state
   * @action validateOrder - validates "order" Prop value
   */
  componentWillLoad() {
    this.gridItemCargoDeprecationWarning();
    this.updateColumnConfig();
    this.validateOrder();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action updateColumnConfig - updates internal column width state (if needed)
   */
  componentDidLoad() {
    if (
      !Array.from(Object.values(this._columnConfig))?.some(
        (val) => val !== undefined
      )
    ) {
      this.updateColumnConfig(); // Double check that "_columnConfig" is populated
    }
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action updateColumnConfig - updates internal column width state
   */
  componentWillUpdate() {
    this.updateColumnConfig();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates "_columnConfig" with current column widths (used to generate
   * "col-..." classes applied to host). Values are extracted from
   * deprecated "gridItemCargo" (if used) or from individual "col-..."
   * Props, then validated.
   */
  updateColumnConfig(): void {
    if (!isNullOrUndefined(this.gridItemCargo)) {
      this._columnConfig = { ...this.gridItemCargo };
    } else {
      this._columnConfig = {
        col: this.col,
        colSm: this.colSm,
        colMd: this.colMd,
        colLg: this.colLg,
        colXl: this.colXl,
      };
    }
    this.validateCols();
  }

  /**
   * Validates each column width in "_columnConfig" - any value that is
   * NOT between 1-12 or "auto" triggers a console warning, then is reset
   * to "undefined".
   */
  validateCols(): void {
    Object.entries(this._columnConfig).forEach(([colSize, value]) => {
      if (!value) return;
      const parsedValue = isNumber(value) ? parseInt(value, 10) : null;
      if (
        (!parsedValue && value !== 'auto' && value !== 'fill') ||
        (!!parsedValue && (parsedValue < 1 || parsedValue > 12))
      ) {
        Utils.consoleWarn(this.host, GRID_ITEM_COLUMN_VALIDATION(colSize));
      }
    });
  }

  /**
   * Validates that "order" Prop value is between 1-12 - if not, outputs
   * a console warning.
   */
  validateOrder(): void {
    if (!this.order) return;
    if (this.order < 1 || this.order > 12) {
      Utils.consoleWarn(this.host, GRID_ITEM_ORDER_VALIDATION);
    }
  }

  /**
   * Generates column width classes (applied to the host element on
   * render) from values within "_columnConfig" object.
   */
  generateColClasses(): string {
    return Object.entries(this._columnConfig).reduce(
      (classString, [colSize, value]) => {
        if (!value) return classString;
        const prefix =
          colSize === 'col' ? 'xs' : colSize.replace(/col/g, '').toLowerCase();
        return `${classString} ${prefix}-${value}`;
      },
      ''
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host
        class={{
          auto: this.auto,
          'pull-right': this.pullRight,
          'pull-left': this.pullLeft,
          [`align-self-${this.alignSelf}`]: !!this.alignSelf,
          [`order-${this.order}`]: !!this.order,
          [`align-${this.alignItems}`]: !!this.alignItems,
          [this.generateColClasses()]: true,
        }}
        data-test-id={`${this.identifier}-grid-item`}>
        <slot />
      </Host>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Sets text alignment within Grid Item.
   * @internal
   */
  @Prop({ mutable: true }) alignItems: FluidAlignment;

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "gridItemCargo" is now deprecated. Instead, use the
   * "config" Prop (allows ALL Grid Item Props to be set). -- Config
   * object that allows all "col-..." Props to be set via a single Prop.
   * @deprecated
   */
  @Prop({ mutable: true }) gridItemCargo: FluidGridItemCargo;

  @Watch('gridItemCargo')
  gridItemCargoDeprecationWarning() {
    if (isNullOrUndefined(this.gridItemCargo)) return;
    Utils.deprecationWarning(
      this.host,
      'Prop',
      'gridItemCargo',
      'config',
      'FluidGridItemConfig',
      'FluidGridItemCargo'
    );
  }
}
